import { Controller } from "@hotwired/stimulus";
import { create as createBookingRoom } from "frontoffice/services/bookingRoomService";
import { remove as removeBookingResourceSku } from "frontoffice/services/bookingResourceSkusService";
import { GroupProfileDomHelpers } from "../services/GroupProfileDomHelpers";

/**
 * Accommodation Controller for Product Group Profile Step
 *
 * Responsibilities:
 * - Handle room selection (select room button clicks)
 * - Handle room removal (remove room button clicks)
 * - Update room visual states (border colors, backgrounds)
 * - Coordinate with validation and price controllers on changes
 * - Manage add/remove room buttons state
 *
 * Events listened:
 * - click on room selection buttons
 * - click on room removal buttons
 *
 * Events dispatched:
 * - group-profile:room-selected - When a room is selected
 * - group-profile:room-removed - When a room is removed
 * - group-profile:validation-changed - When room selection changes validation state
 */
export default class ProductGroupProfileAccommodationController extends Controller {
  static values = {
    bookingId: Number,
    removeButtonText: {
      type: String,
      default: "Remove",
    },
    removeButtonLoadingText: {
      type: String,
      default: "Removing...",
    },
    unknownErrorText: {
      type: String,
      default: "Unknown error",
    },
  };

  connect() {
    this.updateRoomManagementButtons();
  }

  selectRoom(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const { roomId } = button.dataset;
    const { roomAssignmentId } = button.dataset;

    const pathParts = window.location.pathname.split("/");
    const scrambledId = pathParts[pathParts.indexOf("bookings") + 1];

    const form = this.element.closest("form");
    let groupProfileId = null;
    if (form) {
      const groupProfileField = form.querySelector(
        'input[name*="selected_group_profile_id"]'
      );
      if (groupProfileField) {
        groupProfileId = groupProfileField.value;
      }
    }

    const data = {
      resource_sku_id: roomId,
      booking_room_assignment_id: roomAssignmentId,
      booking_scrambled_id: scrambledId,
    };

    if (groupProfileId) {
      data.group_profile_id = groupProfileId;
    }

    button.disabled = true;
    const originalText = button.innerHTML;
    button.innerHTML = `
      <svg class="animate-spin h-4 w-4 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    `;

    const success = () => {
      GroupProfileDomHelpers.dispatchEvent(
        this.element,
        "group-profile:room-selected",
        {
          roomId,
          roomAssignmentId,
        }
      );

      window.location.reload();
    };

    const error = (xhr, status, errorMessage) => {
      button.disabled = false;
      button.innerHTML = originalText;

      const errorData = xhr.responseJSON || { status: errorMessage };

      GroupProfileDomHelpers.dispatchEvent(
        this.element,
        "group-profile:room-selection-failed",
        {
          error: errorData.status || "Unknown error",
        }
      );
    };

    createBookingRoom({ data, success, error });
  }

  removeRoom(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const { bookingResourceSkuId } = button.dataset;

    if (!bookingResourceSkuId || bookingResourceSkuId === "undefined") {
      return;
    }

    button.disabled = true;
    button.textContent = this.removeButtonLoadingTextValue;

    const success = () => {
      GroupProfileDomHelpers.dispatchEvent(
        this.element,
        "group-profile:room-removed",
        {
          bookingResourceSkuId,
        }
      );

      window.location.reload();
    };

    const error = (xhr, status, errorMessage) => {
      button.disabled = false;
      button.textContent = this.removeButtonTextValue;

      let errorText = this.unknownErrorTextValue;
      if (xhr.responseJSON && xhr.responseJSON.status) {
        errorText = xhr.responseJSON.status;
      } else if (xhr.responseText) {
        try {
          const parsed = JSON.parse(xhr.responseText);
          errorText = parsed.status || parsed.error || xhr.responseText;
        } catch (e) {
          errorText = xhr.responseText;
        }
      } else if (errorMessage) {
        errorText = errorMessage;
      }

      GroupProfileDomHelpers.dispatchEvent(
        this.element,
        "group-profile:room-removal-failed",
        {
          error: errorText,
        }
      );
    };

    removeBookingResourceSku({ id: bookingResourceSkuId, success, error });
  }

  handleRoomSelection(event) {
    const checkbox = event.target;
    const { accommodationId } = checkbox.dataset;

    if (checkbox.checked) {
      const roomContainer = checkbox.closest(".border");
      if (roomContainer) {
        roomContainer.classList.add("border-tourrise-blue-500", "bg-blue-50");
        roomContainer.classList.remove("border-gray-200");
      }
    } else {
      const roomContainer = checkbox.closest(".border");
      if (roomContainer) {
        roomContainer.classList.remove(
          "border-tourrise-blue-500",
          "bg-blue-50"
        );
        roomContainer.classList.add("border-gray-200");
      }
    }

    if (accommodationId) {
      GroupProfileDomHelpers.dispatchEvent(
        this.element,
        "group-profile:accommodation-changed",
        {
          accommodationId,
          checked: checkbox.checked,
        }
      );
    }
  }

  updateRoomManagementButtons() {
    const accommodationContainer = this.element.querySelector(".px-3");
    if (!accommodationContainer) return;

    const roomContainers =
      accommodationContainer.querySelectorAll("[data-room-index]");
    const roomCount = roomContainers.length;

    const removeButton = accommodationContainer.querySelector(
      'button[data-action*="removeAccommodationRoom"]'
    );

    if (removeButton) {
      if (roomCount <= 1) {
        removeButton.disabled = true;
        removeButton.classList.add("opacity-50", "cursor-not-allowed");
      } else {
        removeButton.disabled = false;
        removeButton.classList.remove("opacity-50", "cursor-not-allowed");
      }
    }
  }

  getAccommodationGroupId() {
    const accommodationGroupElements =
      this.element.querySelectorAll("[data-group-id]");
    let accommodationGroupId = null;

    accommodationGroupElements.forEach((element) => {
      const accommodationContainer = element.closest(
        ".accommodation-container, [data-accommodation-container]"
      );
      if (accommodationContainer) {
        accommodationGroupId = element.dataset.groupId;
      }
    });

    if (!accommodationGroupId) {
      const selectedRooms = GroupProfileDomHelpers.findSelectedRooms(
        this.element
      );
      if (selectedRooms.length > 0) {
        const accommodationHeaders = this.element.querySelectorAll("h3");
        accommodationHeaders.forEach((header) => {
          const headerText = header.textContent.toLowerCase();
          if (
            headerText.includes("accommodation") ||
            headerText.includes("unterkunft")
          ) {
            const groupIdElement =
              header.closest("[data-group-id]") ||
              header.parentNode.querySelector("[data-group-id]");
            if (groupIdElement) {
              accommodationGroupId = groupIdElement.dataset.groupId;
            }
          }
        });
      }
    }

    return accommodationGroupId;
  }

  countSelectedRooms() {
    const roomAssignmentContainers =
      GroupProfileDomHelpers.findRoomAssignmentContainers(this.element);
    let selectedCount = 0;

    roomAssignmentContainers.forEach((container) => {
      const hasSelection = container.querySelector(
        ".border-green-500.bg-green-50"
      );
      if (hasSelection) {
        selectedCount += 1;
      }
    });

    return {
      selected: selectedCount,
      total: roomAssignmentContainers.length,
    };
  }
}
